import React from "react"
import {
  Box, Flex, Image, Text, Icon as ChakraIcon,
} from "@chakra-ui/react"
import { TextMessage } from "sharedTypes"
import { isImageUrl } from "utilities/whatsappTemplates"
import Icon from "components/elements/Icon"

const CHANNEL_STYLES: Record<TextMessage["channel"], { bg: string, tailColor: string, shadow?: string }> = {
  sms: { bg: "gray.200", tailColor: "#E9E9E9" },
  whatsapp: { bg: "white", tailColor: "white", shadow: "0 1px 0.5px rgba(0,0,0,0.13)" },
}

type Props = {
  text: React.ReactNode
  channel?: TextMessage["channel"]
  mediaUrl?: string
  buttons?: string[]
}

const MediaAttachment = ({ url }: { url: string }) => {
  if (isImageUrl(url)) {
    return (
      <Image
        src={url}
        borderTopRadius="xl"
        w="100%"
        maxH="160px"
        objectFit="contain"
        bg="gray.100"
        mb={1}
      />
    )
  }

  const fileName = url.split("/").pop() || "Document"

  return (
    <Flex
      bg="gray.100"
      borderTopRadius="xl"
      p={3}
      mb={1}
      alignItems="center"
      gap={2}
    >
      <Icon icon="file-icon" size={8} color="gray.500" />
      <Text fontSize="xs" color="gray.600" noOfLines={1} flex={1}>
        {fileName}
      </Text>
    </Flex>
  )
}

const ReplyIcon = () => (
  <ChakraIcon viewBox="0 0 24 24" w={4} h={4} color="whatsapp.600">
    <path
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"
    />
  </ChakraIcon>
)

const QuickReplyButtons = ({ buttons }: { buttons: string[] }) => (
  <Box borderTopWidth={1} borderColor="gray.200">
    {buttons.map((label, i) => (
      <Flex
        key={label}
        borderTopWidth={i > 0 ? 1 : 0}
        borderColor="gray.200"
        py={2}
        justifyContent="center"
        alignItems="center"
        gap={1.5}
      >
        <ReplyIcon />
        <Text fontSize="sm" color="whatsapp.600" fontWeight="medium">
          {label}
        </Text>
      </Flex>
    ))}
  </Box>
)

const TextBubble = ({
  text, channel = "sms", mediaUrl, buttons,
}: Props) => {
  const { bg, tailColor, shadow } = CHANNEL_STYLES[channel]

  return (
    <Box
      position="relative"
      borderRadius="2xl"
      bg={bg}
      maxW="85%"
      boxShadow={shadow}
      _after={{
        content: "\"\"",
        position: "absolute",
        bottom: 3,
        left: 0,
        border: "10px solid",
        borderColor: `transparent transparent transparent ${tailColor}`,
        transform: "translateY(100%)",
      }}
    >
      <Box borderRadius="2xl" overflow="hidden">
        {mediaUrl && <MediaAttachment url={mediaUrl} />}
        <Box p={4} pt={mediaUrl ? 2 : 4}>
          <Text whiteSpace="pre-line">
            {text}
          </Text>
        </Box>
        {buttons && buttons.length > 0 && <QuickReplyButtons buttons={buttons} />}
      </Box>
    </Box>
  )
}

export default TextBubble
